import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { Store, Search, Sparkles } from "lucide-react";
import SeoHead from "@/components/seo/SeoHead";

const Brands = () => {
  const { t } = useI18n();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = brands.filter((b) =>
    !q || b.name.toLowerCase().includes(q.toLowerCase())
  );
  const featured = filtered.filter((b) => b.featured);
  const rest = filtered.filter((b) => !b.featured);

  return (
    <div className="container-fah py-8" data-testid="page-brands">
      <SeoHead
        title="Shop by Brand"
        description="Browse products from trusted agricultural brands on FarmersAgroHub — genuine seeds, pesticides, fertilizers and machinery from verified manufacturers."
        path="/brands"
      />

      <div className="mb-6">
        <div className="inline-flex items-center gap-2 bg-accent text-accent-foreground rounded-full px-3 py-1 text-xs font-semibold mb-2">
          <Store className="w-3.5 h-3.5" /> {t("Shop by Brand")}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">
          {t("All Brands")} <span className="text-muted-foreground text-lg font-normal">({brands.length})</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t("Discover trusted agricultural brands and their genuine products.")}
        </p>
      </div>

      <div className="flex items-center gap-2 bg-white border border-border rounded-lg px-3 py-2 max-w-md mb-6">
        <Search className="w-4 h-4 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("Search brands…")}
          className="flex-1 text-sm focus:outline-none bg-transparent"
          data-testid="brands-search"
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-square bg-muted/60 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {featured.length > 0 && (
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-secondary" />
                <h2 className="font-display font-bold text-lg">{t("Featured Brands")}</h2>
              </div>
              <BrandGrid brands={featured} />
            </section>
          )}
          <section>
            {featured.length > 0 && (
              <h2 className="font-display font-bold text-lg mb-3">{t("All Brands")}</h2>
            )}
            {rest.length === 0 && filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {t("No brands found.")}
              </div>
            ) : (
              <BrandGrid brands={rest.length ? rest : filtered} />
            )}
          </section>
        </>
      )}
    </div>
  );
};

const BrandGrid = ({ brands }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="brands-grid">
    {brands.map((b) => (
      <Link
        key={b.id}
        to={`/brand/${b.slug}`}
        className="card-fah p-4 flex flex-col items-center text-center hover-lift transition group"
        data-testid={`brand-card-${b.slug}`}
      >
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-accent/40 border border-border overflow-hidden flex items-center justify-center mb-3 group-hover:border-primary transition">
          {b.logo_url ? (
            <img src={b.logo_url} alt={b.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl font-display font-bold text-primary">
              {b.name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="font-semibold text-sm line-clamp-2">{b.name}</div>
        <div className="text-[11px] text-muted-foreground mt-1">
          {b.product_count} {b.product_count === 1 ? "product" : "products"}
        </div>
      </Link>
    ))}
  </div>
);

export default Brands;
